import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import MediationRoundedIcon from '@mui/icons-material/MediationRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import { Button, Stack } from '@mui/material';
import React, { FC, memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, NodeProps, Position } from 'reactflow';

import { TelegramMessageType } from '../../../interfaces/common';
import { IMenuFlowNodeData } from '../interfaces';

export interface IMenuNodeData {
  onDelete: () => void;
  position: { x: number; y: number };
  data: IMenuFlowNodeData;
  next: null;
  onNodePositionChange: (
    nodeId: string,
    newPosition: { x: number; y: number },
  ) => void;
  _id: string;
}

interface MenuNodeProps extends Omit<NodeProps, 'data'> {
  data: IMenuNodeData;
}

const MenuNode: FC<MenuNodeProps> = memo(({ data, xPos, yPos }) => {
  useEffect(() => {
    data.onNodePositionChange(data._id, {
      x: xPos,
      y: yPos,
    });
  }, [xPos, yPos]);

  const { t } = useTranslation();

  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = nodeRef.current;
    const preventZoom = (event: WheelEvent) => event.stopPropagation();

    if (node) {
      node.addEventListener('wheel', preventZoom);
      return () => {
        node.removeEventListener('wheel', preventZoom);
      };
    }
  }, []);

  return (
    <Stack
      sx={{
        position: 'relative',
      }}
    >
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{
          width: '100%',
          height: '36px',
        }}
      >
        <Button
          className="red"
          sx={{
            width: '36px',
            height: '36px',
            minWidth: '36px',
            mb: '6px',
          }}
          onClick={data.onDelete}
        >
          <CloseRoundedIcon
            sx={{
              color: 'grey.1',
            }}
          />
        </Button>
      </Stack>
      <Stack
        sx={{
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'green.2',
          backgroundColor: 'grey.15',
          width: '180px',
          overflow: 'hidden',
          cursor: 'default',
        }}
        ref={nodeRef}
      >
        <Handle
          type="target"
          position={Position.Left}
          id={'edge-' + data._id}
          style={{
            background: 'transparent',
            borderColor: 'transparent',
            height: '100%',
            width: '100%',
            borderRadius: '0',
            zIndex: -1,
          }}
        />
        <Stack direction="row" alignItems="center">
          <Stack
            sx={{
              width: '100%',
            }}
          >
            <Button
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '0',
                justifyContent: 'start',
                color: 'grey.1',
                borderBottom: '1px solid',
                borderColor: 'grey.12',
                backgroundColor: 'grey.15',
                '&:hover': { backgroundColor: 'green.2', color: 'grey.15' },
              }}
              startIcon={<ChatBubbleRoundedIcon sx={{ fontSize: '18px' }} />}
              onClick={() => {
                data.onDelete();
                data.data.addTelegramMessageNode(
                  {
                    text: '',
                    buttons: [],
                    media: [],
                    telegramMessageType: TelegramMessageType.media,
                  },
                  data.position,
                  data._id,
                );
              }}
            >
              {t('flowNodes.messageLabel')}
            </Button>
            <Button
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '0',
                justifyContent: 'start',
                color: 'grey.1',
                borderBottom: '1px solid',
                borderColor: 'grey.12',
                backgroundColor: 'grey.15',
                '&:hover': { backgroundColor: 'green.2', color: 'grey.15' },
              }}
              startIcon={<BoltRoundedIcon sx={{ fontSize: '18px' }} />}
              onClick={() => {
                data.onDelete();
                data.data.addActionsNode([], data.position, data._id);
              }}
            >
              {t('flowNodes.actionsLabel')}
            </Button>
            <Button
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '0',
                justifyContent: 'start',
                color: 'grey.1',
                borderBottom: '1px solid',
                borderColor: 'grey.12',
                backgroundColor: 'grey.15',
                '&:hover': { backgroundColor: 'green.2', color: 'grey.15' },
              }}
              startIcon={<MediationRoundedIcon sx={{ fontSize: '18px' }} />}
              onClick={() => {
                data.onDelete();
                data.data.addConditionsNode([], data.position, data._id);
              }}
            >
              {t('flowNodes.conditionsLabel')}
            </Button>
            <Button
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '0',
                justifyContent: 'start',
                color: 'grey.1',
                borderBottom: '1px solid',
                borderColor: 'grey.12',
                backgroundColor: 'grey.15',
                '&:hover': { backgroundColor: 'green.2', color: 'grey.15' },
              }}
              startIcon={<TimerRoundedIcon sx={{ fontSize: '18px' }} />}
              onClick={() => {
                data.onDelete();
                data.data.addDelayNode(
                  {
                    delayForSeconds: 0,
                    applyTimeRange: false,
                    days: [],
                    startTime: '',
                    endTime: '',
                    timezone: '',
                  },
                  data.position,
                  data._id,
                );
              }}
            >
              {t('flowNodes.delayLabel')}
            </Button>
            <Button
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '0',
                justifyContent: 'start',
                color: 'grey.1',
                borderBottom: '1px solid',
                borderColor: 'grey.12',
                backgroundColor: 'grey.15',
                '&:hover': { backgroundColor: 'green.2', color: 'grey.15' },
              }}
              startIcon={<FmdBadIcon sx={{ fontSize: '18px' }} />}
              onClick={() => {
                data.onDelete();
                data.data.addChatActionNode(
                  {
                    actionType: 'typing',
                    duration: 1,
                  },
                  data.position,
                  data._id,
                );
              }}
            >
              {t('flowNodes.chatAction')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
});

MenuNode.displayName = 'MenuNode';

export default MenuNode;
